import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import dayjs from "dayjs";
import { capitalize } from "../../actprep-ui/utils/formatting";
import { actSubjects } from "../../actprep-ui/constants/program";

const BooleanChip = ({ value, label }) => (
  <Chip
    icon={value ? <CheckCircleIcon /> : <CancelIcon />}
    label={label}
    color={value ? "success" : "error"}
    size="small"
  />
);

const MetricCard = ({ title, value, subtitle, hit }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h6">{value}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      {hit !== undefined && (
        <Box sx={{ mt: 1 }}>
          <BooleanChip value={hit} label={hit ? "Hit" : "Miss"} />
        </Box>
      )}
    </CardContent>
  </Card>
);

const RoleRowDetails = ({ data }) => (
  <Box>
    {/* Coach Info */}
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={data.label}
        subheader={`${data.role} • Week of ${dayjs(data.week.start_date).format(
          "MMM D, YYYY"
        )}`}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          Coach: {data.coach.name || "N/A"}
        </Typography>
      </CardContent>
    </Card>

    {/* Metrics Grid */}
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <MetricCard
          title="Check-ins"
          value={`${data.check_ins.count} / 7`}
          subtitle="Days checked in this week"
          hit={data.check_ins.bonus_hit}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <MetricCard
          title="On-Time Submissions"
          value={`${data.time_submissions.on_time_count} / 5`}
          subtitle="Time submissions on time"
          hit={data.time_submissions.bonus_hit}
        />
      </Grid>
    </Grid>

    {/* Check-ins Table */}
    {data.check_ins.items.length > 0 && (
      <Card sx={{ mt: 2 }}>
        <CardHeader title="Check-ins" />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.check_ins.items.map((item) => (
                  <TableRow key={item.uuid}>
                    <TableCell>
                      {dayjs(item.date).format("MMM D, YYYY")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    )}

    {/* Time Submissions Table */}
    {data.time_submissions.items.length > 0 && (
      <Card sx={{ mt: 2 }}>
        <CardHeader title="Time Submissions" />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>On Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.time_submissions.items.map((item) => (
                  <TableRow key={item.uuid}>
                    <TableCell>
                      {dayjs(item.date).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell>
                      <BooleanChip
                        value={item.on_time}
                        label={item.on_time ? "Yes" : "No"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    )}
  </Box>
);

const StudentRowDetails = ({ data }) => {
  if (!data.has_week) {
    return (
      <Box>
        <Card>
          <CardHeader
            title={data.name}
            subheader={`Week of ${dayjs(data.week.start_date).format(
              "MMM D, YYYY"
            )}`}
          />
          <CardContent>
            <Typography color="text.secondary">
              No week data available for this student.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Student Info */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={data.name}
          subheader={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                Week {data.week.week_number} • {capitalize(data.week.subject)}
              </Typography>
              {data.week.is_fast_track && (
                <Chip label="Fast Track" size="small" color="primary" />
              )}
              {!data.week.in_the_game && (
                <Chip
                  label={`Out of Game: ${
                    data.week.out_of_game_reason || "Unknown"
                  }`}
                  size="small"
                  color="warning"
                />
              )}
            </Stack>
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {dayjs(data.week.start_date).format("MMM D")} -{" "}
            {dayjs(data.week.end_date).format("MMM D, YYYY")}
          </Typography>
        </CardContent>
      </Card>

      {/* Hours & Score Metrics */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <MetricCard
            title="Hours"
            value={`${data.hours.actual_hours} / ${data.hours.five_point_hours_needed}`}
            subtitle={
              data.hours.is_hours_volunteer ? "Hours Volunteer" : "Regular"
            }
            hit={data.hours.hit_hours}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {data.week.is_review_week ? (
            <MetricCard
              title={"Total Points Increased"}
              value={`${data.score.score_reached || 0} / ${
                data.score.score_target || 0
              }`}
              subtitle={`Completed subjects: ${data.completed_subjects
                .map(capitalize)
                .join(", ")}`}
              hit={data.score.hit_score}
            />
          ) : (
            <MetricCard
              title={`${
                data.week.subject.charAt(0).toUpperCase() +
                data.week.subject.slice(1)
              } Score`}
              value={`${data.score.actual_score || 0} / ${
                data.score.five_point_score_needed || 0
              }`}
              subtitle={`Start: ${data.score.start_score || 0} | Increase: +${
                data.score.increase
              }`}
              hit={data.score.hit_score}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <MetricCard
            title={"Quiz Score"}
            value={`${data.week.quiz_score ?? 0}%`}
            hit={data.week.quiz_score >= 80}
          />
        </Grid>
      </Grid>

      {/* Subject Scores */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Subject Scores" />
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell>Program Start</TableCell>
                  <TableCell>Week Start</TableCell>
                  <TableCell>5PI Goal</TableCell>
                  <TableCell>Current Best</TableCell>
                  <TableCell>Increase</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actSubjects.map((subject) => {
                  const programStart = data.scores[`start_${subject}`];
                  const weekStart = data.scores[`initial_${subject}`];
                  const best = data.scores[`best_${subject}`];
                  const increase = best - programStart;
                  const fivePointGoal =
                    data.scores[`five_point_goal_${subject}`];

                  return (
                    <TableRow key={subject}>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {subject}
                      </TableCell>
                      <TableCell>{programStart || 0}</TableCell>
                      <TableCell>{weekStart || 0}</TableCell>
                      <TableCell>{fivePointGoal || 0}</TableCell>
                      <TableCell>{best || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={`+${increase}`}
                          size="small"
                          color={increase > 0 ? "success" : "default"}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Bottom row with composite/total label, averages for score columns */}
                {(() => {
                  // Reusable average calculation function
                  const avg = (keyTemplate) => {
                    const values = actSubjects
                      .map((subject) => data.scores[keyTemplate(subject)])
                      .filter((v) => typeof v === "number" && !isNaN(v));
                    if (!values.length) return 0;
                    return Math.round(
                      values.reduce((a, b) => a + b, 0) / values.length
                    );
                  };

                  const avgProgramStart = avg((s) => `start_${s}`);
                  const avgWeekStart = avg((s) => `initial_${s}`);
                  const avgFivePointGoal = avg((s) => `five_point_goal_${s}`);
                  const avgBest = avg((s) => `best_${s}`);

                  return (
                    <TableRow>
                      <TableCell>
                        <strong>Composite/Total</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{avgProgramStart}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{avgWeekStart}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{avgFivePointGoal}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{avgBest}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`+${data.scores.total_increase}`}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Increase this week: +{data.scores.total_increase_this_week}
          </Typography>
        </CardContent>
      </Card>

      {/* Awards This Week */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Awards This Week" />
        <CardContent>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <BooleanChip
              value={data.awards_this_week.foundation}
              label="Foundation"
            />
            <BooleanChip
              value={data.awards_this_week.persistence}
              label="Persistence"
            />
            <BooleanChip
              value={data.awards_this_week.determination}
              label="Determination"
            />
            <BooleanChip
              value={data.awards_this_week.accuracy}
              label="Accuracy"
            />
            <BooleanChip
              value={data.awards_this_week.gold_award}
              label="Gold Award"
            />
            <BooleanChip value={data.awards_this_week.letter} label="Letter" />
          </Stack>
        </CardContent>
      </Card>

      {/* On Track All Weeks */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="On Track for All Weeks" />
        <CardContent>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <BooleanChip
              value={data.on_track_all_weeks.foundation}
              label="Foundation"
            />
            <BooleanChip
              value={data.on_track_all_weeks.persistence}
              label="Persistence"
            />
            <BooleanChip
              value={data.on_track_all_weeks.determination}
              label="Determination"
            />
            <BooleanChip
              value={data.on_track_all_weeks.accuracy}
              label="Accuracy"
            />
            <BooleanChip
              value={data.on_track_all_weeks.gold_award}
              label="Gold Award"
            />
            <BooleanChip
              value={data.on_track_all_weeks.letter}
              label="Letter"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Daily Stats */}
      {data.daily_stats && data.daily_stats.length > 0 && (
        <Card>
          <CardHeader title="Daily Stats" />
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Check-in</TableCell>
                    <TableCell>Before Noon</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell>Pages</TableCell>
                    <TableCell>Mem Time</TableCell>
                    <TableCell>Worksheet Q{"'"}s</TableCell>
                    <TableCell>Cheatsheet Q{"'"}s</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.daily_stats.map((stat) => (
                    <TableRow key={stat.uuid}>
                      <TableCell>{dayjs(stat.date).format("MMM D")}</TableCell>
                      <TableCell>{stat.did_check_in ? "✓" : "✗"}</TableCell>
                      <TableCell>
                        {stat.did_check_in_before_noon ? "✓" : "✗"}
                      </TableCell>
                      <TableCell>{stat.hours_worked || 0}</TableCell>
                      <TableCell>{stat.pages_read || 0}</TableCell>
                      <TableCell>{stat.memorization_time || 0}</TableCell>
                      <TableCell>{stat.worksheet_questions || 0}</TableCell>
                      <TableCell>{stat.cheat_sheet_questions || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default function MasterRowDetailsModal({
  open,
  onClose,
  rowType,
  rowId,
  startDate,
}) {
  const { data, isLoading, error } = useGet(
    apiUrls.admin.masterV2Row(rowType, rowId),
    {
      enabled: open && !!rowType && !!rowId,
      params: {
        start_date: startDate,
      },
    }
  );

  const rowData = data?.data;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: { maxHeight: "90vh" },
        },
      }}
    >
      <DialogTitle>Row Details</DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error">
            Error loading row details: {error.message}
          </Typography>
        )}
        {rowData && !isLoading && (
          <>
            {rowData.row_type === "role" && <RoleRowDetails data={rowData} />}
            {rowData.row_type === "student" && (
              <StudentRowDetails data={rowData} />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
